import React from "react";
import { LegalLayout, Section } from "../components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How we collect, store, and use your data. Compliant with the UK GDPR and Data Protection Act 2018."
      lastUpdated="14 February 2026"
    >
      <Section id="controller" title="1. Who We Are">
        <p>
          PitchIQ (the &quot;Service&quot;, &quot;we&quot;, &quot;us&quot;) is operated from the United Kingdom.
          For data protection purposes, we are the <strong>data controller</strong> of personal information
          collected through pitchiq.win.
        </p>
        <p>Contact: <a href="mailto:info@pitchiq.win" className="text-primary-yellow underline">info@pitchiq.win</a></p>
      </Section>

      <Section id="data-collected" title="2. What Data We Collect">
        <p>We only collect what we need to run the Service:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Account data</strong> — your email, name, chosen username, hashed password (we never see your real password), favourite club.</li>
          <li><strong>Activity data</strong> — predictions you submit, points earned, leagues you join, login timestamps.</li>
          <li><strong>Payment data</strong> — when you buy Premier, Stripe processes the payment on our behalf. We receive only the transaction status and a session ID. <strong>We do not see your card number, CVV, or billing address.</strong></li>
          <li><strong>Technical data</strong> — IP address (briefly, for rate-limiting and abuse prevention), browser type, device type.</li>
        </ul>
      </Section>

      <Section id="lawful-basis" title="3. Lawful Basis for Processing">
        <p>We rely on these UK GDPR lawful bases:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Contract</strong> — to provide the Service you signed up for.</li>
          <li><strong>Legitimate interest</strong> — to send essential service emails (e.g. matchweek reminders), prevent fraud, and improve the product.</li>
          <li><strong>Consent</strong> — you may opt out of non-essential emails any time. Withdrawing consent does not affect prior processing.</li>
        </ul>
      </Section>

      <Section id="emails" title="4. Emails We Send You">
        <p>You will receive these transactional emails:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Email verification on signup.</li>
          <li>Password reset (only when requested).</li>
          <li>Pre-kickoff reminders (24h before each matchweek closes for predictions).</li>
          <li>Post-matchweek results digest (your points + rank).</li>
          <li>Payment receipts via Stripe (from <code>@pitchiq.win</code>).</li>
        </ul>
        <p>
          Email reminders can be disabled in your <a href="/profile" className="text-primary-yellow underline">Profile</a> (coming soon),
          or by replying &quot;unsubscribe&quot; to any email.
        </p>
      </Section>

      <Section id="third-parties" title="5. Who We Share Data With">
        <p>We share minimal data only with the following processors:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Stripe Payments Europe Ltd.</strong> — payment processing (UK-regulated, PCI-DSS Level 1).</li>
          <li><strong>Google (Gmail SMTP)</strong> — to deliver transactional emails.</li>
          <li><strong>API-Sports</strong> — to fetch fixtures (no user data sent — read-only).</li>
          <li><strong>MongoDB Atlas</strong> — encrypted database hosting (UK / EU region where available).</li>
        </ul>
        <p>We do <strong>not sell, rent, or trade</strong> your personal data to any third party. Ever.</p>
      </Section>

      <Section id="cookies" title="6. Cookies & Tracking">
        <p>We use the minimum cookies necessary to run the Service:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Authentication token</strong> — kept in browser localStorage, not a cookie. Used to keep you logged in. Expires after 30 days.</li>
          <li><strong>Cloudflare security cookie</strong> (<code>__cf_bm</code>) — set by our hosting provider to block automated abuse. Expires after 30 minutes.</li>
        </ul>
        <p>
          We do <strong>not</strong> use third-party analytics, advertising trackers, or social media pixels at present.
          If we add advertising in future (e.g. Google AdSense), this policy will be updated and you will be asked for consent first.
        </p>
      </Section>

      <Section id="retention" title="7. How Long We Keep Data">
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Account data</strong> — kept while your account is active. Deleted within 30 days of account closure.</li>
          <li><strong>Predictions &amp; leaderboard history</strong> — kept indefinitely in aggregated/anonymised form for season archives, even after account closure.</li>
          <li><strong>Payment records</strong> — kept for 7 years to meet UK accounting/HMRC obligations.</li>
          <li><strong>Email tokens (verify/reset)</strong> — deleted within 48 hours of use or expiry.</li>
        </ul>
      </Section>

      <Section id="your-rights" title="8. Your Rights Under UK GDPR">
        <p>You can, at any time, ask us to:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Access</strong> the data we hold about you (Subject Access Request).</li>
          <li><strong>Correct</strong> inaccurate data via your Profile or by emailing us.</li>
          <li><strong>Delete</strong> your account and personal data (&quot;right to be forgotten&quot;).</li>
          <li><strong>Restrict</strong> or object to certain processing.</li>
          <li><strong>Export</strong> your data in a portable format (predictions + account info).</li>
        </ul>
        <p>
          Email <a href="mailto:info@pitchiq.win" className="text-primary-yellow underline">info@pitchiq.win</a> with
          your request. We&apos;ll respond within 30 days as required by law.
        </p>
        <p>
          If you&apos;re unhappy with our response, you have the right to complain to the UK Information Commissioner&apos;s Office (ICO):
          <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary-yellow underline ml-1">ico.org.uk</a>.
        </p>
      </Section>

      <Section id="security" title="9. Security">
        <p>We protect your data with industry-standard measures:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Passwords hashed with <strong>bcrypt</strong> (one-way, never reversible).</li>
          <li>All traffic encrypted with <strong>TLS 1.2+</strong> (HTTPS-only).</li>
          <li>Database encrypted at rest.</li>
          <li>No card data ever stored on our servers — Stripe handles all payment data under PCI-DSS Level 1.</li>
        </ul>
      </Section>

      <Section id="children" title="10. Children">
        <p>
          PitchIQ is not intended for users under <strong>18</strong>. We do not knowingly collect personal data
          from anyone under 18. If you believe a minor has registered, please email us and we will delete the account.
        </p>
      </Section>

      <Section id="changes" title="11. Changes to This Policy">
        <p>
          We may update this policy as our service evolves. Material changes will be announced via email at least
          14 days before they take effect. The &quot;Last updated&quot; date at the top reflects the most recent revision.
        </p>
      </Section>

      <Section id="contact" title="12. Contact">
        <p>
          Privacy questions or data requests: <a href="mailto:info@pitchiq.win" className="text-primary-yellow underline">info@pitchiq.win</a>.
        </p>
      </Section>
    </LegalLayout>
  );
}
